import { useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import type {
  AnalyticsCockpitLayout,
  AnalyticsGoalInput,
  AnalyticsMetricKey,
  AnalyticsQueryFilters,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  AnalyticsDateFilter,
  defaultAnalyticsFilters,
} from "@/components/analytics/date-filter";
import { AnalyticsDashboardGrid } from "@/components/analytics/dashboard-grid";
import {
  BarChart,
  FunnelChart,
  KpiCard,
  MetricsTable,
  METRIC_LABELS,
  formatMetric,
  resultToCsv,
} from "@/components/analytics/metric-widgets";
import {
  useAnalyticsCatalog,
  useAnalyticsCockpit,
  useAnalyticsDashboard,
  useAnalyticsDashboards,
  useAnalyticsGoals,
  useAnalyticsPreferences,
  useAnalyticsReports,
  useCreateAnalyticsDashboard,
  useCreateAnalyticsGoal,
  useCreateAnalyticsReport,
  useRunAnalyticsReport,
  useSetDefaultDashboard,
  useUpdateAnalyticsDashboard,
  useUpdateAnalyticsGoal,
  useUpdateAnalyticsPreferences,
} from "@/hooks/useAnalytics";
import { postAnalyticsAiInsights } from "@/api/analytics";

const TABS = [
  { to: "/analytics", label: "Overview", end: true },
  { to: "/analytics/dashboards", label: "Dashboards" },
  { to: "/analytics/reports", label: "Reports" },
  { to: "/analytics/goals", label: "Goals" },
  { to: "/analytics/cockpit", label: "Cockpit" },
];

export function AnalyticsLayout() {
  return (
    <div className="space-y-5 pb-10">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Sales Dashboard
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pipeline performance, outbound activity, and goals for SDRs and sales
          leaders. Action queue stays on Home (Today).
        </p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

/** @deprecated use nested Analytics routes */
export function AnalyticsPage() {
  return <Navigate to="/analytics" replace />;
}

export function AnalyticsOverviewPage() {
  const navigate = useNavigate();
  const prefs = useAnalyticsPreferences();
  const catalog = useAnalyticsCatalog();
  const createDash = useCreateAnalyticsDashboard();
  const createReport = useCreateAnalyticsReport();
  const createGoal = useCreateAnalyticsGoal();
  const [filters] = useState<AnalyticsQueryFilters>(defaultAnalyticsFilters);
  const [insight, setInsight] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  const defaultId =
    prefs.data?.defaultDashboardId ?? "catalog:deal-analytics";
  const defaultDash = useAnalyticsDashboard(
    defaultId.startsWith("catalog:") || defaultId.length > 0
      ? defaultId
      : "catalog:deal-analytics",
  );

  useEffect(() => {
    let cancelled = false;
    setInsightLoading(true);
    setInsightError(null);
    void postAnalyticsAiInsights(
      "Summarize pipeline health, win rate, and the top risk for this week in 2-3 short sentences.",
    )
      .then((res) => {
        if (!cancelled) setInsight(res.answer);
      })
      .catch((err) => {
        if (!cancelled) {
          setInsightError(
            err instanceof Error ? err.message : "Could not load insights",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setInsightLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const recent = prefs.data?.recentIds ?? [];
  const dashboards = catalog.data?.dashboards ?? [];
  const reports = catalog.data?.reports ?? [];

  const recentLabels = recent.map((id) => {
    const cid = id.startsWith("catalog:") ? id.slice("catalog:".length) : "";
    const d = cid ? dashboards.find((x) => x.id === cid) : undefined;
    const r = cid ? reports.find((x) => x.id === cid) : undefined;
    const label = d?.name ?? r?.name ?? (cid || id.slice(0, 8));
    const href = r
      ? `/analytics/reports/${id.startsWith("catalog:") ? id : `catalog:${r.id}`}`
      : `/analytics/dashboards/${id}`;
    return { id, label, href };
  });

  const widgets =
    defaultDash.data?.dashboard && "widgets" in defaultDash.data.dashboard
      ? defaultDash.data.dashboard.widgets
      : [];

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-border bg-muted/15 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          AI insights
        </p>
        {insightLoading ? (
          <p className="mt-1 text-sm text-muted-foreground">Generating…</p>
        ) : insightError ? (
          <p className="mt-1 text-sm text-muted-foreground">{insightError}</p>
        ) : (
          <p className="mt-1 text-sm text-foreground/90">{insight}</p>
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        <Link
          to="/analytics/dashboards/catalog:deal-analytics"
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
        >
          Deal analytics
        </Link>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-2 text-sm"
          disabled={createDash.isPending}
          onClick={async () => {
            const item = await createDash.mutateAsync({
              catalogId: "outbound",
              name: "Outbound dashboard",
            });
            navigate(`/analytics/dashboards/${item.dashboardId}`);
          }}
        >
          Create dashboard
        </button>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-2 text-sm"
          disabled={createReport.isPending}
          onClick={async () => {
            const item = await createReport.mutateAsync({
              catalogId: "email-funnel",
            });
            navigate(`/analytics/reports/catalog:email-funnel`);
            void item;
          }}
        >
          Create report
        </button>
        <button
          type="button"
          className="rounded-md border border-border px-3 py-2 text-sm"
          disabled={createGoal.isPending}
          onClick={() => navigate("/analytics/goals")}
        >
          Create goal
        </button>
        <Link
          to="/"
          className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          Back to Today
        </Link>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Recently used
        </h2>
        {recentLabels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Open a dashboard or report to see it here.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {recentLabels.map((r) => (
              <li key={r.id}>
                <Link
                  to={r.href}
                  className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40"
                >
                  {r.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Pipeline snapshot
          </h2>
          <Link
            to={`/analytics/dashboards/${defaultId}`}
            className="text-sm text-brand hover:underline"
          >
            Open full view
          </Link>
        </div>
        {defaultDash.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <AnalyticsDashboardGrid widgets={widgets} filters={filters} />
        )}
      </section>
    </div>
  );
}

export function AnalyticsDashboardsPage() {
  const [q, setQ] = useState("");
  const [apolloOnly, setApolloOnly] = useState(false);
  const list = useAnalyticsDashboards();
  const createDash = useCreateAnalyticsDashboard();
  const navigate = useNavigate();

  const catalog = list.data?.catalog ?? [];
  const custom = list.data?.items ?? [];

  const filteredCatalog = catalog.filter((d) => {
    if (apolloOnly) return true;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (
      d.name.toLowerCase().includes(needle) ||
      d.description.toLowerCase().includes(needle) ||
      d.category.toLowerCase().includes(needle)
    );
  });

  const filteredCustom = custom.filter((d) => {
    if (apolloOnly) return d.source === "apollo_prebuilt";
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return d.name.toLowerCase().includes(needle);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search dashboards…"
          className="w-64 rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={apolloOnly}
            onChange={(e) => setApolloOnly(e.target.checked)}
          />
          Created by Apollo
        </label>
        <button
          type="button"
          className="ml-auto rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
          disabled={createDash.isPending}
          onClick={async () => {
            const item = await createDash.mutateAsync({
              catalogId: "outbound",
            });
            navigate(`/analytics/dashboards/${item.dashboardId}`);
          }}
        >
          Clone Outbound
        </button>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Pre-built
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCatalog.map((d) => (
            <Link
              key={d.id}
              to={`/analytics/dashboards/catalog:${d.id}`}
              className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
            >
              <p className="text-xs text-muted-foreground">{d.category}</p>
              <p className="mt-1 font-medium text-foreground">{d.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {d.description}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {filteredCustom.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Your dashboards
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCustom.map((d) => (
              <Link
                key={d.dashboardId}
                to={`/analytics/dashboards/${d.dashboardId}`}
                className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
              >
                <p className="text-xs text-muted-foreground">
                  {d.source === "apollo_prebuilt" ? "Apollo" : "Custom"}
                </p>
                <p className="mt-1 font-medium text-foreground">{d.name}</p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function AnalyticsDashboardDetailPage() {
  const { dashboardId = "" } = useParams();
  const [filters, setFilters] = useState<AnalyticsQueryFilters>(
    defaultAnalyticsFilters,
  );
  const dash = useAnalyticsDashboard(dashboardId);
  const setDefault = useSetDefaultDashboard();
  const updateDash = useUpdateAnalyticsDashboard();
  const prefs = useAnalyticsPreferences();

  const dashboard = dash.data?.dashboard;
  const widgets =
    dashboard && "widgets" in dashboard ? dashboard.widgets : [];
  const name = dashboard && "name" in dashboard ? dashboard.name : "Dashboard";
  const isFavorite =
    prefs.data?.favoriteDashboardIds?.includes(dashboardId) ||
    ("favorite" in (dashboard ?? {}) &&
      Boolean((dashboard as { favorite?: boolean }).favorite));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/analytics/dashboards"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Dashboards
          </Link>
          <h2 className="mt-1 text-xl font-semibold">{name}</h2>
          {dashboard && "description" in dashboard && dashboard.description ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {dashboard.description}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            disabled={updateDash.isPending}
            onClick={() =>
              updateDash.mutate({
                id: dashboardId,
                patch: { favorite: !isFavorite },
              })
            }
          >
            {isFavorite ? "Unfavorite" : "Favorite"}
          </button>
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            disabled={setDefault.isPending}
            onClick={() => setDefault.mutate(dashboardId)}
          >
            Set default
          </button>
        </div>
      </div>

      <AnalyticsDateFilter value={filters} onChange={setFilters} />

      {dash.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading dashboard…</p>
      ) : dash.isError ? (
        <p className="text-sm text-destructive">Failed to load dashboard.</p>
      ) : (
        <AnalyticsDashboardGrid widgets={widgets} filters={filters} />
      )}
    </div>
  );
}

export function AnalyticsReportsPage() {
  const [q, setQ] = useState("");
  const list = useAnalyticsReports();
  const catalog = list.data?.catalog ?? [];
  const custom = list.data?.items ?? [];

  const filtered = catalog.filter(
    (r) =>
      !q.trim() ||
      r.name.toLowerCase().includes(q.trim().toLowerCase()) ||
      r.category.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search reports…"
        className="w-64 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((r) => (
          <Link
            key={r.id}
            to={`/analytics/reports/catalog:${r.id}`}
            className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
          >
            <p className="text-xs text-muted-foreground">{r.category}</p>
            <p className="mt-1 font-medium text-foreground">{r.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
          </Link>
        ))}
        {custom.map((r) => (
          <Link
            key={r.reportId}
            to={`/analytics/reports/${r.reportId}`}
            className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
          >
            <p className="text-xs text-muted-foreground">Custom</p>
            <p className="mt-1 font-medium text-foreground">{r.name}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function AnalyticsReportDetailPage() {
  const { reportId = "" } = useParams();
  const [filters, setFilters] = useState<AnalyticsQueryFilters>(
    defaultAnalyticsFilters,
  );
  const run = useRunAnalyticsReport(reportId, { filters });
  const report = run.data?.report;
  const result = run.data?.result;
  const metrics =
    report && "metricKeys" in report ? report.metricKeys : ([] as AnalyticsMetricKey[]);
  const viz = report && "visualization" in report ? report.visualization : "table";
  const name = report && "name" in report ? report.name : "Report";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/analytics/reports"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Reports
          </Link>
          <h2 className="mt-1 text-xl font-semibold">{name}</h2>
          {report && "description" in report && report.description ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {report.description}
            </p>
          ) : null}
        </div>
        {result ? (
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={() => {
              const blob = new Blob([resultToCsv(result)], {
                type: "text/csv",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${reportId}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Export CSV
          </button>
        ) : null}
      </div>

      <AnalyticsDateFilter value={filters} onChange={setFilters} />

      <div className="rounded-lg border border-border bg-card p-5">
        {run.isLoading ? (
          <p className="text-sm text-muted-foreground">Running report…</p>
        ) : run.isError || !result ? (
          <p className="text-sm text-destructive">Failed to run report.</p>
        ) : viz === "funnel" ? (
          <FunnelChart metrics={metrics} totals={result.totals} />
        ) : viz === "bar" || viz === "line" ? (
          <BarChart result={result} metrics={metrics} />
        ) : viz === "kpi" ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((m) => (
              <KpiCard
                key={m}
                label={METRIC_LABELS[m] ?? m}
                value={formatMetric(m, result.totals[m] ?? 0)}
              />
            ))}
          </div>
        ) : (
          <MetricsTable result={result} metrics={metrics} />
        )}
      </div>
    </div>
  );
}

const GOAL_METRICS: AnalyticsMetricKey[] = [
  "emails_replied",
  "emails_sent",
  "calls_logged",
  "calls_connected",
  "deals_won",
  "meetings_booked",
  "tasks_completed",
];

export function AnalyticsGoalsPage() {
  const goals = useAnalyticsGoals();
  const createGoal = useCreateAnalyticsGoal();
  const updateGoal = useUpdateAnalyticsGoal();
  const [form, setForm] = useState<AnalyticsGoalInput>({
    name: "",
    metricKey: "emails_replied",
    target: 25,
    period: "month",
    ownerScope: "me",
  });

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Create goal
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-muted-foreground">
            Name
            <input
              className="mt-1 block w-48 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Reply goal"
            />
          </label>
          <label className="text-xs text-muted-foreground">
            Metric
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.metricKey}
              onChange={(e) =>
                setForm({
                  ...form,
                  metricKey: e.target.value as AnalyticsMetricKey,
                })
              }
            >
              {GOAL_METRICS.map((m) => (
                <option key={m} value={m}>
                  {METRIC_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Target
            <input
              type="number"
              min={1}
              className="mt-1 block w-24 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.target}
              onChange={(e) =>
                setForm({ ...form, target: Number(e.target.value) || 1 })
              }
            />
          </label>
          <label className="text-xs text-muted-foreground">
            Period
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.period ?? "month"}
              onChange={(e) =>
                setForm({
                  ...form,
                  period: e.target.value as AnalyticsGoalInput["period"],
                })
              }
            >
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </select>
          </label>
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
            disabled={createGoal.isPending || !form.name.trim()}
            onClick={() => createGoal.mutate(form)}
          >
            Save goal
          </button>
        </div>
      </section>

      <section className="space-y-3">
        {(goals.data?.goals ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No goals yet. Track emails replied or deals won to start.
          </p>
        ) : (
          (goals.data?.goals ?? []).map((g) => {
            const pct = Math.min(
              100,
              Math.round((g.progress / Math.max(g.target, 1)) * 100),
            );
            return (
              <div
                key={g.goalId}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-foreground">{g.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {METRIC_LABELS[g.metricKey]} · {g.period} · {g.status}
                    </p>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {g.progress} / {g.target}
                  </div>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted/20">
                  <div
                    className="h-full rounded-full bg-brand"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {g.status === "active" ? (
                  <button
                    type="button"
                    className="mt-3 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      updateGoal.mutate({
                        id: g.goalId,
                        patch: { status: "archived" },
                      })
                    }
                  >
                    Archive
                  </button>
                ) : null}
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}

export function AnalyticsCockpitPage() {
  const prefs = useAnalyticsPreferences();
  const updatePrefs = useUpdateAnalyticsPreferences();
  const layout = (prefs.data?.cockpitLayout ??
    "sales_rep") as AnalyticsCockpitLayout;
  const cockpit = useAnalyticsCockpit(layout);
  const week = cockpit.data?.week;
  const metrics: AnalyticsMetricKey[] = useMemo(
    () => [
      "emails_sent",
      "emails_replied",
      "calls_logged",
      "calls_connected",
      "tasks_completed",
      "meetings_booked",
      "deals_won",
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Layout</span>
        {(
          [
            ["sales_rep", "Sales Rep"],
            ["sales_manager", "Sales Manager"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={
              layout === id
                ? "rounded-md bg-brand/10 px-3 py-1.5 text-sm font-medium text-brand"
                : "rounded-md border border-border px-3 py-1.5 text-sm"
            }
            onClick={() => updatePrefs.mutate({ cockpitLayout: id })}
          >
            {label}
          </button>
        ))}
      </div>

      {cockpit.isLoading || !week ? (
        <p className="text-sm text-muted-foreground">Loading cockpit…</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((m) => (
              <KpiCard
                key={m}
                label={METRIC_LABELS[m]}
                value={formatMetric(m, week.totals[m] ?? 0)}
              />
            ))}
            <KpiCard
              label="Tasks due / open"
              value={String(cockpit.data?.tasksOpen ?? 0)}
            />
          </div>

          {layout === "sales_manager" ? (
            <p className="text-sm text-muted-foreground">
              Manager view shows the same week totals for your workspace user
              scope. Team rollups expand when teammates share data.
            </p>
          ) : null}

          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              High-performing sequences
            </h3>
            {(cockpit.data?.sequences ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No sequences yet.</p>
            ) : (
              <ul className="space-y-2">
                {(cockpit.data?.sequences ?? []).map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-foreground">{s.name}</span>
                    <span className="text-muted-foreground">
                      {s.enrollments} enrolled · {s.replies} replies
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
